import React, { memo } from 'react';
import type { WorkOrderStatus } from '../../../../enums/workOrderStatus';
import { ARIA_PX_PER_REM, COLUMN_MAX_REM, COLUMN_MIN_REM, RESIZE_STEP_REM, STATUS_LABELS } from '../constants';
import styles from './WorkOrderStatusList.module.scss';

interface ColumnResizeHandleProps {
  status: WorkOrderStatus;
  onResizeStart: (status: WorkOrderStatus, event: React.PointerEvent<HTMLElement>) => void;
  onNudge: (status: WorkOrderStatus, deltaRem: number) => void;
}

const KEY_DELTAS: Record<string, number> = { ArrowLeft: -RESIZE_STEP_REM, ArrowRight: RESIZE_STEP_REM };

// aria-valuenow is written by useColumnWidths alongside the width.
const ColumnResizeHandle = ({ status, onResizeStart, onNudge }: ColumnResizeHandleProps) => {
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    onResizeStart(status, event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const delta = KEY_DELTAS[event.key];
    if (!delta) return;
    event.preventDefault();
    onNudge(status, delta);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${STATUS_LABELS[status]} column`}
      aria-valuemin={COLUMN_MIN_REM * ARIA_PX_PER_REM}
      aria-valuemax={COLUMN_MAX_REM * ARIA_PX_PER_REM}
      tabIndex={0}
      data-resize-status={status}
      className={styles.resizeHandle}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
    />
  );
};

export default memo(ColumnResizeHandle);
